/**
 * utils/secureFlag.js
 *
 * Tamper-evident storage for client auth flags (isAdmin, accountRole,
 * tabsAccessible) kept in sessionStorage.
 *
 * Each value is stored as `base64(json).signature`, where the signature is a
 * keyed hash of (SECRET + token + json). Because the signature is bound to the
 * user's token, a user cannot:
 *   - hand-edit the JSON (signature stops matching → read returns null), or
 *   - paste another user's signed blob (their token differs → mismatch).
 *
 * ⚠️ This is obfuscation, NOT security. SECRET ships in the bundle, so a
 * determined attacker can recompute signatures, mutate Redux at runtime, or
 * call the admin APIs directly. Real authorization MUST be enforced
 * server-side on the admin endpoints. This only blocks casual sessionStorage
 * tampering.
 */

const SECRET = "stepout-flag-v1-Kp7vR2";

/** Weak-but-sufficient sync keyed hash (double FNV-1a → 16 hex chars). */
function keyedHash(text) {
  let h1 = 0x811c9dc5;
  let h2 = 0xc59d1c81;
  const s = SECRET + "|" + text;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x85ebca77);
  }
  const hex = (n) => (n >>> 0).toString(16).padStart(8, "0");
  return hex(h1) + hex(h2);
}

/**
 * Sign a value bound to the token.
 * @returns {string} storable blob
 */
export function signFlag(value, token) {
  const json = JSON.stringify(value ?? null);
  const b64 = btoa(unescape(encodeURIComponent(json)));
  const sig = keyedHash((token ?? "") + "|" + json);
  return `${b64}.${sig}`;
}

/**
 * Verify and decode a signed value. Returns null if missing/tampered.
 * @returns {*} the original value, or null
 */
export function verifyFlag(raw, token) {
  if (!raw || typeof raw !== "string" || !raw.includes(".")) return null;
  try {
    const [b64, sig] = raw.split(".");
    const json = decodeURIComponent(escape(atob(b64)));
    if (keyedHash((token ?? "") + "|" + json) !== sig) return null;
    return JSON.parse(json);
  } catch {
    return null;
  }
}
